import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import Immutable from 'immutable';

import {
  UI_OPERATION_STATUS_SUCCESS,
  UI_OPERATION_STATUS_ERROR,
  setOperationStatus,
} from '@uncinc/react-drupal-core';

import {
  Button,
  Container,
  FormRadioButtons,
} from '@uncinc/uncinc-react-kitchen-sink';

import CustomFormikWrapper from '../../../components/CustomFormikWrapper';
import PersonalInformationSection from './Components/PersonalInformationSection';
import BillingAddressSection from './Components/BillingAddressSection';
import DeliveryAddressSection from './Components/DeliveryAddressSection';
import checkoutSubmit from './helpers/checkoutSubmit';
import createSignupSchema from './helpers/checkoutValidation';
import selectAddress from './helpers/addressHelpers';
import { getPhysicalCheckoutInitialValues } from './helpers/checkoutInitialValues';
import CartSummary from '../CartSummary';
import DiscountCodeSection from '../DiscountCodeSection';

import { formatCartItemsForAnalytics } from '../../../helpers/commerce';
import {
  clearPageInformation,
  setPageInformation,
  setPageViewSent,
  getNewsletterContactPropertyOptions,
} from '../../../store/actions/UI';

import type {
  CheckoutFormValues,
  AnalyticsItem,
  CartData,
  CheckoutReduxState,
  AddressData,
} from './types/checkout.types';

import '../style.scss';
import './style.scss';
import { DEFAULT_LANGUAGE } from '../../../config/constants';

/**
 * Checkout page component
 *
 * Manages the checkout form flow including:
 * - Personal information collection
 * - Billing address entry
 * - Delivery address selection
 * - Form validation and submission
 * - Redux state management
 *
 * @component
 */
const CheckoutPage = (): React.ReactElement | null => {
  const PAGE_TITLE = 'Gegevens | VeiligheidNL';
  const dispatch = useDispatch();
  const history = useHistory();
  const [selectedAddressIndex, setSelectedAddressIndex] = useState<
  number | null
  >(null);
  const [selectedBillingAddressIndex, setSelectedBillingAddressIndex] =
    useState<number | null>(null);
  // TODO: Replace 'any' with proper FormikProps type when available
  const formRef = useRef<any>(null);
  const { t } = useTranslation();

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const state = useSelector(
    (reduxState: CheckoutReduxState) => (reduxState as any).toJS?.() || reduxState,
  );

  const loggedIn = state.login?.loggedIn === true;
  const checkoutStatus = state.ui?.commerce?.operations?.cart_checkout?.status;
  const cart: CartData = state.commerce?.cart || {};
  const uid = state.login?.uid as string;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  const userState = state.user as CheckoutReduxState['user'];

  const totalPrice =
    state.commerce?.cart?.content?.attributes?.order_total?.subtotal?.number ||
    0;
  const analyticsItems: AnalyticsItem[] =
    formatCartItemsForAnalytics(
      Immutable.fromJS(state.commerce?.cart?.products || []),
    ) || [];
  const customerEmail = userState?.[uid]?.mail?.[0]?.value ?? '';
  const debtorNumber =
    userState?.[uid]?.field_external_relation_number?.[0]?.value ?? '';
  const debtor = userState?.[uid]?.debtors?.[debtorNumber] ?? {};
  const billingAddress = (userState?.[uid]?.profile
    ?.field_billing_address?.[0] ?? {}) as AddressData;
  const firstName =
    userState?.[uid]?.profile?.field_first_name?.[0]?.value ?? '';
  const lastName = [
    userState?.[uid]?.profile?.field_infix_name?.[0]?.value,
    userState?.[uid]?.profile?.field_last_name?.[0]?.value,
  ]
    .filter(Boolean)
    .join(' ');
  const phoneNumber =
    userState?.[uid]?.profile?.field_telephone?.[0]?.value ?? '';

  const defaultAddressIndexStr =
    userState?.[uid]?.profile?.field_default_address_index?.[0]?.value;
  const defaultAddressIndex = defaultAddressIndexStr
    ? Number(defaultAddressIndexStr)
    : null;

  const defaultBillingIndexStr =
    userState?.[uid]?.profile?.field_default_billing_index?.[0]?.value;
  const defaultBillingIndex = defaultBillingIndexStr
    ? Number(defaultBillingIndexStr)
    : null;

  const billingEmail =
    userState?.[uid]?.profile?.field_billing_email_address?.[0]?.value ||
    debtor.invoiceEmail ||
    cart.content?.attributes?.field_invoice_email ||
    customerEmail ||
    '';

  const billingReference =
    userState?.[uid]?.profile?.field_billing_reference?.[0]?.value ||
    cart.content?.attributes?.field_customer_reference ||
    '';

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const addressesRaw: Immutable.List<any> = useSelector((reduxState: any) => reduxState.getIn(['user', uid, 'profile', 'address'], Immutable.List()));

  const addresses = useMemo(
    () => addressesRaw.toJS() as AddressData[],
    [addressesRaw],
  );

  // Spotler options.
  const spotlerUserContact = state.uiInternal?.newsletter?.contact;

  useEffect(() => {
    if (loggedIn === false) {
      history.replace('/cart/checkout/login-or-register');
      return;
    }

    if (checkoutStatus === UI_OPERATION_STATUS_SUCCESS) {
      history.push('/cart/checkout/confirm');
      dispatch(setOperationStatus('commerce', 'cart_checkout', null, null));
    }
  }, [dispatch, history, checkoutStatus, loggedIn]);

  useEffect(() => {
    dispatch(clearPageInformation());
    dispatch(setPageInformation(PAGE_TITLE));
    dispatch(setPageViewSent(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initialValues: CheckoutFormValues = getPhysicalCheckoutInitialValues({
    customerEmail,
    firstName,
    lastName,
    phoneNumber,
    billingEmail,
    billingReference,
    billingAddress,
    debtor,
    cart,
    defaultAddressIndex,
    addresses,
  });

  const onSubmit = (formValues: CheckoutFormValues, form: any): void => {
    checkoutSubmit(formValues, form, {
      totalPrice,
      analyticsItems,
      dispatch,
    });
  };

  useEffect(() => {
    if (
      defaultAddressIndex !== null &&
      selectedAddressIndex === null &&
      formRef &&
      addressesRaw &&
      addressesRaw.size > 0
    ) {
      const formSelectedAddress = addressesRaw.get(defaultAddressIndex);
      setSelectedAddressIndex(defaultAddressIndex);
      selectAddress(formRef, formSelectedAddress);
    }
  }, [defaultAddressIndex, selectedAddressIndex, addressesRaw, formRef]);

  useEffect(() => {
    if (
      defaultBillingIndex !== null &&
      selectedBillingAddressIndex === null &&
      formRef &&
      addressesRaw &&
      addressesRaw.size > 0
    ) {
      const formSelectedBillingAddress = addressesRaw.get(defaultBillingIndex);
      setSelectedBillingAddressIndex(defaultBillingIndex);
      selectAddress(formRef, formSelectedBillingAddress, 'billing_');
    }
  }, [defaultBillingIndex, selectedBillingAddressIndex, addressesRaw, formRef]);

  // Try to fetch our Spotler account if we are logged in.
  useEffect(() => {
    if (!spotlerUserContact) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      // @ts-ignore - Redux thunk dispatch returns function, not AnyAction
      dispatch(getNewsletterContactPropertyOptions());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Prevent form from being initialized before we have all the required data.
  if (!customerEmail || !cart || !cart.content) {
    return null;
  }

  return (
    <>
      <Helmet
        htmlAttributes={{
          lang: DEFAULT_LANGUAGE,
        }}
      >
        <title>{PAGE_TITLE}</title>
      </Helmet>
      {/* <StepsProgressBar webShopCurrentPath={currentPath} /> */}
      <Container className="signUpForm">
        <Link to="/cart">
          <div className="Cart__backToPage">
            <div className="icon icon-chevron-right" />
            {t('cart.back')}
          </div>
        </Link>

        {checkoutStatus === UI_OPERATION_STATUS_ERROR && (
          <div className="Form__error">{t('checkout.error_message')}</div>
        )}

        <CustomFormikWrapper
          onSubmit={onSubmit}
          initialValues={initialValues}
          validationSchema={createSignupSchema(t)}
          innerRef={formRef}
        >
          {({ values }: { values: CheckoutFormValues }) => (
            <div className="Cart__container">
              <div className="Cart__content">
                <fieldset>
                  <legend>{t('checkout.my_information')}</legend>
                  <FormRadioButtons
                    title={null}
                    fieldName="business_option"
                    options={{
                      business: t('checkout.business'),
                      personal: t('checkout.personal'),
                    }}
                    defaultValue="business"
                  />
                </fieldset>
                <PersonalInformationSection customerEmail={customerEmail} />
                <BillingAddressSection
                  values={values}
                  formRef={formRef}
                  selectAddress={selectAddress}
                  selectedBillingAddressIndex={selectedBillingAddressIndex}
                  setSelectedBillingAddressIndex={
                    setSelectedBillingAddressIndex
                  }
                  addressesRaw={addressesRaw}
                  defaultBillingIndex={defaultBillingIndex}
                />
                <DeliveryAddressSection
                  selectedAddressIndex={selectedAddressIndex}
                  values={values}
                  defaultAddressIndex={defaultAddressIndex}
                  setSelectedAddressIndex={setSelectedAddressIndex}
                  addressesRaw={addressesRaw}
                  formRef={formRef}
                  selectAddress={selectAddress}
                />
              </div>

              <div className="Cart_summary">
                <div className="CartSummary">
                  <div className="CartSummary__wrapper">
                    <div className="CartSummary__title">
                      <strong>{t('cart.your_order')}</strong>
                    </div>
                    <CartSummary showProducts />
                    <DiscountCodeSection variant="v1" />
                  </div>
                  <Button type="submit" className="Button Button--primary">
                    {t('cart.further_to_checkout')}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CustomFormikWrapper>
      </Container>
    </>
  );
};

export default CheckoutPage;
